import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => el.classList.add('visible'), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

export default function AboutPage() {
  const storyImg1 = useReveal(0);
  const storyText1 = useReveal(100);
  const storyText2 = useReveal(0);
  const storyImg2 = useReveal(100);
  const storyImg3 = useReveal(0);
  const storyText3 = useReveal(100);
  const valuesHeading = useReveal(0);
  const valueCard0 = useReveal(0);
  const valueCard1 = useReveal(120);
  const valueCard2 = useReveal(240);
  const expectHeading = useReveal(0);
  const expectItem0 = useReveal(0);
  const expectItem1 = useReveal(80);
  const expectItem2 = useReveal(160);
  const expectItem3 = useReveal(240);
  const expectItem4 = useReveal(320);
  const expectItem5 = useReveal(400);
  const personalTouch = useReveal(0);
  const ctaBlock = useReveal(0);

  const valueCards = [
    { icon: 'ri-shield-check-line', title: 'Honesty', desc: "I'll tell you what you need to know, not just what you want to hear. My goal is to help you make informed decisions, even if it means being the voice of caution." },
    { icon: 'ri-lightbulb-line', title: 'Knowledge', desc: "My inspection background means you get insights most agents can't provide. I understand home systems, potential issues, and what repairs actually cost." },
    { icon: 'ri-hand-heart-line', title: 'Service', desc: "Your goals come first. I'm here to guide, not pressure. Whether you're buying, selling, or just exploring, I'll meet you where you are." },
  ];
  const valueRefs = [valueCard0, valueCard1, valueCard2];

  const expectItems = [
    { title: 'Clear Communication', desc: "I'll keep you informed every step of the way, answering questions promptly and explaining the process clearly." },
    { title: 'Honest Assessments', desc: "I'll give you my professional opinion on properties and market conditions, even when it's not what you want to hear." },
    { title: 'Inspection-Level Insights', desc: "During showings, I'll point out things most agents miss—from foundation concerns to HVAC issues to electrical red flags." },
    { title: 'Patient Guidance', desc: "Especially for first-time buyers, I'll take the time to explain everything and make sure you feel confident in your decisions." },
    { title: 'Smooth Coordination', desc: "For complex situations like estates, probate, or buy-sell transitions, I'll handle the details so you don't have to stress." },
    { title: 'Follow-Up Support', desc: "Even after closing, I'm here if you need recommendations, advice, or just have questions about your home." },
  ];
  const expectRefs = [expectItem0, expectItem1, expectItem2, expectItem3, expectItem4, expectItem5];

  return (
    <div className="min-h-screen bg-[#FAF9F7] page-enter">

      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/2ad1b98d49c3aa5a0fe1924b07af2e39.webp')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-6 text-center text-white">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 animate-fade-in delay-100 font-medium">About Your Realtor</p>
          <h1 className="font-cormorant text-4xl sm:text-6xl lg:text-7xl font-light mb-6 animate-fade-in-up delay-200 leading-tight">
            Real Knowledge. <em className="not-italic font-semibold">Real Results.</em>
          </h1>
          <p className="text-base sm:text-lg text-white/90 leading-relaxed animate-fade-in-up delay-300 max-w-2xl mx-auto">
            I'm Kendrick Taylor, and I believe moving should be easier when you work with someone who truly understands homes.
          </p>
        </div>
      </section>

      {/* Story Block 1 */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <div ref={storyImg1} className="reveal from-left rounded-lg overflow-hidden">
              <img src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/3d668c0b1227b7a3a7ad4df0d6acff88.jpeg" alt="Kendrick's early career" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover object-top" />
            </div>
            <div ref={storyText1} className="reveal from-right">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Chapter One</p>
              <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-light text-[#111111] mb-6 leading-tight">
                From Criminal Justice<br /><em className="not-italic font-semibold">to a New Calling</em>
              </h2>
              <div className="w-10 h-0.5 bg-[#C8923A] mb-6"></div>
              <p className="text-[#555] leading-relaxed">
                I graduated from Ferris State University with a degree in criminal justice and worked for the Mecosta County Sheriff's Office. I loved serving my community, but a career-ending car accident changed everything. I had to find a new path — one that would allow me to continue helping people in a meaningful way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Block 2 */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <div ref={storyText2} className="reveal from-left order-2 lg:order-1">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Chapter Two</p>
              <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-light text-[#111111] mb-6 leading-tight">
                Discovering<br /><em className="not-italic font-semibold">Home Inspection</em>
              </h2>
              <div className="w-10 h-0.5 bg-[#C8923A] mb-6"></div>
              <p className="text-[#555] leading-relaxed">
                During a home inspection on a property I was interested in, the inspector offered me a job. I spent three years learning homes inside and out — foundations, HVAC, electrical, plumbing, roofing. I became the person who could tell you what was really going on with a house, not just what it looked like on the surface.
              </p>
            </div>
            <div ref={storyImg2} className="reveal from-right order-1 lg:order-2 rounded-lg overflow-hidden">
              <img src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/58e2809b108af933c77c6f5bd4848f28.jpeg" alt="Kendrick doing home inspection" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* Story Block 3 */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-14 items-center">
            <div ref={storyImg3} className="reveal from-left rounded-lg overflow-hidden">
              <img src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/47f4d9c7e98065b9f28557e4dcdbc036.jpeg" alt="Kendrick with clients" className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover object-top" />
            </div>
            <div ref={storyText3} className="reveal from-right">
              <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Chapter Three</p>
              <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-light text-[#111111] mb-6 leading-tight">
                Why I Became<br /><em className="not-italic font-semibold">a Realtor</em>
              </h2>
              <div className="w-10 h-0.5 bg-[#C8923A] mb-6"></div>
              <p className="text-[#555] leading-relaxed mb-5">
                I saw too many agents who didn't understand the homes they were selling. Buyers were making huge decisions without real information. Sellers were leaving money on the table because they didn't know what to fix or highlight.
              </p>
              <p className="text-[#555] leading-relaxed">
                I knew I could do better — and help people make smarter, less stressful moves. That's why I became a realtor: to combine my inspection knowledge with a genuine desire to serve and guide people through one of life's biggest transitions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div ref={valuesHeading} className="reveal text-center mb-10 sm:mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Core Values</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">What Drives Me</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {valueCards.map((v, i) => (
              <div key={i} ref={valueRefs[i]} className="reveal scale text-center p-8 sm:p-10 bg-white rounded-lg hover-lift">
                <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center">
                  <i className={`${v.icon} text-4xl text-[#1C3A6B]`}></i>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-[#111111] mb-3">{v.title}</h3>
                <div className="w-8 h-0.5 bg-[#C8923A] mx-auto mb-5"></div>
                <p className="text-sm text-[#666] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6">
          <div ref={expectHeading} className="reveal text-center mb-10 sm:mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C8923A] mb-4 font-medium">Working Together</p>
            <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-[#111111]">What It's Like to Work With Me</h2>
          </div>
          <div className="space-y-3 sm:space-y-4">
            {expectItems.map((item, i) => (
              <div key={i} ref={expectRefs[i]} className="reveal flex items-start gap-4 p-5 sm:p-6 bg-[#FAF9F7] rounded-lg hover-lift border border-gray-100">
                <i className="ri-checkbox-circle-fill text-xl text-[#C8923A] mt-1 flex-shrink-0"></i>
                <div>
                  <h3 className="font-semibold text-[#111111] mb-1">{item.title}</h3>
                  <p className="text-sm text-[#666]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personal Note */}
      <section className="py-14 sm:py-20 bg-[#FAF9F7]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6">
          <div ref={personalTouch} className="reveal scale bg-white rounded-lg p-8 sm:p-12 border border-gray-100 text-center">
            <h2 className="font-cormorant text-2xl sm:text-3xl font-semibold text-[#111111] mb-6">A Little More About Me</h2>
            <p className="text-[#666] leading-relaxed">
              I'm based in Temperance, Michigan, and I'm grateful to serve the communities of Southeast Michigan and nearby Ohio. My faith and family keep me grounded, and I believe real estate is about more than transactions — it's about helping people move forward in life. Whether you're buying your first home, selling a family estate, or looking for hunting land, I'm here to make the process as smooth and stress-free as possible.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-20 bg-[#111111]">
        <div ref={ctaBlock} className="reveal max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-cormorant text-4xl sm:text-5xl font-light text-white mb-6">Let's Work Together</h2>
          <p className="text-white/70 mb-8 sm:mb-10">Whether you're buying, selling, or just exploring your options, I'd love to hear from you.</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a href="tel:+15176629155" className="px-7 sm:px-8 py-3.5 sm:py-4 bg-[#C8923A] text-white font-semibold text-sm rounded-md hover:bg-[#B07B2A] transition-all whitespace-nowrap inline-flex items-center justify-center gap-2 hover-lift">
              <i className="ri-phone-line"></i> Call Kendrick
            </a>
            <Link to="/contact" className="px-7 sm:px-8 py-3.5 sm:py-4 border border-white/30 text-white font-semibold text-sm rounded-md hover:bg-white/10 transition-all whitespace-nowrap hover-lift">
              Send a Message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
